import React, { useEffect, useState, useRef, useCallback } from "react";
import nodata from "../../assets/img/no-data.svg";
import { directBonusHistory } from "../../api/apiService";
import { useToken } from "../../components/TokenContext";
import Web3 from "web3";
import { useAppKitAccount } from "@reown/appkit/react";
import Loader from "../../components/Loader";

const DirectBonus = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [copiedWallet, setCopiedWallet] = useState(null);
  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [web3, setWeb3] = useState(null);

  const { token, fetchToken } = useToken();
  const { address, isConnected } = useAppKitAccount();
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const rowsPerPage = process.env.REACT_APP_ROW_PER_PAGE || 100;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const toggleFilter = () => setShowFilter(!showFilter);
  const closeFilter = () => setShowFilter(false);

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatBalance = (balance) =>
    balance ? parseFloat(balance).toLocaleString() : "0";

  const copyWallet = (wallet) => {
    navigator.clipboard.writeText(wallet);
    setCopiedWallet(wallet);
    setTimeout(() => setCopiedWallet(null), 2000);
  };

  // ✅ Initialize Web3 when wallet connects
  useEffect(() => {
    if (isConnected && address && window.ethereum) {
      const web3Instance = new Web3(window.ethereum);
      setWeb3(web3Instance);
    }
  }, [isConnected, address]);

  // ✅ Fetch Direct Bonus Data
  const fetchDirectBonus = useCallback(
    async (page, searchField, searchValue) => {
      setLoading(true);
      try {
        let currentToken = token || (await fetchToken());
        if (!currentToken) return;

        const loginToken =
          localStorage.getItem("LoginToken") || currentToken;

        const response = await directBonusHistory(
          currentToken,
          loginToken,
          page,
          rowsPerPage,
          searchField,
          searchValue,
          fromDate,
          toDate
        );

        if (response.status) {
          const dataList = response.DataList || [];

          // ✅ Convert string time → number
          const updatedList = dataList.map((row) => ({
            ...row,
            remainingTime: parseInt(row.REMAININGSECONDS || 0, 10),
          }));

          setTableData(updatedList);
          setTotalRecords(response.TotalRecords || dataList.length);
        } else {
          setTableData([]);
          setTotalRecords(0);
        }
      } catch (error) {
        setTableData([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, token, fetchToken]
  );

  // ✅ Initial fetch
  useEffect(() => {
    fetchDirectBonus(currentPage, searchField, searchValue);
  }, [fetchDirectBonus, currentPage]);

  // ✅ Live countdown timer (updates every second)
  useEffect(() => {
    const timer = setInterval(() => {
      setTableData((prevData) =>
        prevData.map((row) => ({
          ...row,
          remainingTime:
            row.remainingTime > 0
              ? Math.max(parseInt(row.remainingTime, 10) - 1, 0)
              : 0,
        }))
      );
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchDirectBonus(1, searchField, searchValue);
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setSearchField("");
    setSearchValue("");
    setCurrentPage(1);
    fetchDirectBonus(1, "", "");
  };



  // ✅ Format Time as DD HH MM
  const formatTime = (s) => {
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);

    return `${days}d ${hours.toString().padStart(2, "0")}h ${minutes
      .toString()
      .padStart(2, "0")}m`;
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="buyshheikh-page">
      <div className="pageTitle">
        Direct Bonus
        <button className="mobile-filter-btn d-md-none" onClick={toggleFilter}>
          Filter <i className="fa fa-filter"></i>
        </button>
      </div>

      {alert.message && (
        <div className={`alert alert-${alert.type} text-center`} role="alert">
          {alert.message}
        </div>
      )}

      <div className="card mb-3">
        <div className="card-body">
          {showFilter && <div className="overlay" onClick={closeFilter}></div>}

          {/* ✅ Table */}
          <div className="table-responsive">
            <table className="table table-striped table-bordered mytable">
              <thead className="table-dark">
                <tr>
                  <th>S.No.</th>
                  <th>Date</th>
                  <th>Wallet</th>
                  {/* <th>Purchase Id</th> */}
                  <th>Purchased Amount($)</th>
                  <th>Bonus Per(%)</th>
                  <th>Bonus</th>
                  {/* <th>Action</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      
                      <Loader />
                    </td>
                  </tr>
                ) : tableData.length ? (
                  tableData.map((row) => (
                    <tr key={row.RowNumber}>
                      <td>{row.RowNumber}</td>
                      <td>{formatDateTime(row.DATE)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span>{row.FROMWALLET}</span>
                          <span
                            className="copy-walletaddress"
                            onClick={() => copyWallet(row.FROMWALLET)}
                          >
                            <i
                              className={`fa ${
                                copiedWallet === row.FROMWALLET
                                  ? "fa-check"
                                  : "fa-copy"
                              }`}
                            ></i>
                          </span>
                        </div>
                      </td>
                      {/* <td>{row.TXNID}</td> */}
                      <td>{formatBalance(row.BASEAMOUNT)}</td>
                      <td>{row.PERCENTAGE}</td>
                      <td>{formatBalance(row.COMM_AMOUNT)}</td>
                      {/* <td>
                        {row.ISWITHDRAWAL === 1 ? (
                          <button className="btn btn-sm btn-secondary" disabled>
                            Claimed
                          </button>
                        ) : row.remainingTime > 0 ? (
                          <button className="btn btn-sm btn-warning" disabled>
                            Wait {formatTime(row.remainingTime)}
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleClaim(row.TXNID)}
                          >
                            Claim Now
                          </button>
                        )}
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <img src={nodata} className="not-data" alt="No Data" />
                      <div>No records found</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Pagination */}
          {tableData.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <div>
                Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, totalRecords)} of{" "}
                {totalRecords} entries
              </div>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, idx) => (
                  <li
                    key={idx}
                    className={`page-item ${
                      currentPage === idx + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => paginate(idx + 1)}
                    >
                      {idx + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectBonus;
